import { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Download, QrCode, ArrowSquareOut, X } from "@phosphor-icons/react";

export default function QRList() {
  const [items, setItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewQr, setViewQr] = useState(null);

  const [form, setForm] = useState({ label: "", destination_type: "page", page_id: "", custom_url: "", active: true });

  const load = async () => {
    setLoading(true);
    try {
      const [qrRes, pagesRes] = await Promise.all([api.get("/qr"), api.get("/pages")]);
      setItems(qrRes.data);
      setPages(pagesRes.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.label.trim()) { toast.error("Label is required"); return; }
    if (form.destination_type === "page" && !form.page_id) { toast.error("Choose a page"); return; }
    if (form.destination_type === "url" && !form.custom_url) { toast.error("Enter a URL"); return; }
    try {
      await api.post("/qr", form);
      toast.success("QR code created");
      setShowForm(false);
      setForm({ label: "", destination_type: "page", page_id: "", custom_url: "", active: true });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (q) => {
    try {
      await api.put(`/qr/${q.id}`, { active: !q.active });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this QR code?")) return;
    try {
      await api.delete(`/qr/${id}`);
      toast.success("QR deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const download = async (q, fmt) => {
    try {
      const token = localStorage.getItem("bv_token") || "";
      const resp = await fetch(`${API_BASE}/qr/${q.id}/image?fmt=${fmt}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `qr-${q.code}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div data-testid="admin-qr-list">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 24 }}>
        <div>
          <div className="overline">QR Engine</div>
          <h1 className="font-display" style={{ fontSize: 36, fontWeight: 900, margin: "8px 0 0", letterSpacing: "-0.03em" }}>QR Codes</h1>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)} data-testid="new-qr-btn">
          <Plus size={14} weight="bold" /> New QR
        </button>
      </div>

      <div className="card">
        {loading ? <div style={{ padding: 32 }} className="skel" /> :
        items.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-2)" }}>
            No QR codes yet.
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr><th>Label</th><th>Code</th><th>Destination</th><th>Status</th><th>Scans</th><th style={{ width: 1 }}>Actions</th></tr>
            </thead>
            <tbody>
              {items.map((q) => {
                const dest = q.destination_type === "page"
                  ? (pages.find((p) => p.id === q.page_id)?.title || "—")
                  : q.custom_url;
                return (
                  <tr key={q.id} data-testid={`qr-row-${q.id}`}>
                    <td style={{ fontWeight: 600 }}>{q.label}</td>
                    <td><code style={{ fontSize: 12 }}>{q.code}</code></td>
                    <td style={{ fontSize: 13 }}>
                      <span className="badge badge-muted" style={{ marginRight: 6 }}>{q.destination_type}</span>
                      {dest}
                    </td>
                    <td>
                      <button onClick={() => toggleActive(q)} data-testid={`qr-toggle-${q.id}`}
                        className={"badge " + (q.active ? "badge-success" : "badge-muted")} style={{ cursor: "pointer", border: 0 }}>
                        {q.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td style={{ fontWeight: 700 }}>{q.scans || 0}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setViewQr(q)} data-testid={`qr-preview-btn-${q.id}`}>
                        <QrCode size={12} />
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => download(q, "png")} data-testid={`qr-dl-png-${q.id}`}>
                        <Download size={12} /> PNG
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => download(q, "svg")} data-testid={`qr-dl-svg-${q.id}`}>
                        SVG
                      </button>
                      <a className="btn btn-ghost btn-sm" href={q.target_url} target="_blank" rel="noreferrer" data-testid={`qr-open-${q.id}`}>
                        <ArrowSquareOut size={12} />
                      </a>
                      <button className="btn btn-ghost btn-sm" onClick={() => del(q.id)} data-testid={`qr-delete-${q.id}`}>
                        <Trash size={12} color="var(--danger)" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create modal */}
      {showForm && (
        <Modal onClose={() => setShowForm(false)} title="New QR Code">
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <label className="label">Label</label>
              <input className="input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Main Entrance" data-testid="qr-label-input" />
            </div>
            <div>
              <label className="label">Destination type</label>
              <select className="select" value={form.destination_type}
                onChange={(e) => setForm({ ...form, destination_type: e.target.value })} data-testid="qr-form-type">
                <option value="page">Information page</option>
                <option value="url">Custom URL</option>
              </select>
            </div>
            {form.destination_type === "page" ? (
              <div>
                <label className="label">Information page</label>
                <select className="select" value={form.page_id} onChange={(e) => setForm({ ...form, page_id: e.target.value })} data-testid="qr-page-select">
                  <option value="">— Select a page —</option>
                  {pages.map((p) => <option key={p.id} value={p.id}>{p.title} ({p.status})</option>)}
                </select>
              </div>
            ) : (
              <div>
                <label className="label">Custom URL</label>
                <input className="input" value={form.custom_url}
                  onChange={(e) => setForm({ ...form, custom_url: e.target.value })} placeholder="https://…" data-testid="qr-form-url" />
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
              <button className="btn" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={create} data-testid="qr-create-submit">Create QR</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Preview modal */}
      {viewQr && (
        <Modal onClose={() => setViewQr(null)} title={viewQr.label}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
            <img alt="qr" data-testid="qr-preview-image" src={`${API_BASE}/qr/${viewQr.id}/image?fmt=png`} style={{ width: 280, height: 280, border: "1px solid var(--border)" }} />
            <div style={{ fontSize: 12, color: "var(--text-2)", textAlign: "center", wordBreak: "break-all" }}>{viewQr.target_url}</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" onClick={() => download(viewQr, "png")}><Download size={14} /> PNG</button>
              <button className="btn" onClick={() => download(viewQr, "svg")}><Download size={14} /> SVG</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, title }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "grid", placeItems: "center", zIndex: 50, padding: 24 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: "100%", maxWidth: 480, background: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" }}>
          <div className="overline">{title}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} data-testid="modal-close"><X size={14} /></button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}
